import React, { useState, useEffect, useCallback } from 'react';
import SMSLayout from '../components/SMSLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Archive, Search, Download, RefreshCw, Calendar, Filter, X,
  MessageSquare, CheckCircle, XCircle, Eye, Clock, Loader2,
  TrendingUp, Send, Users, ChevronLeft, ChevronRight, FileDown
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-slate-500', icon: Clock },
  scheduled: { label: 'Programmé', color: 'bg-blue-500', icon: Calendar },
  sent: { label: 'Envoyé', color: 'bg-emerald-500', icon: Send },
  mock_sent: { label: 'Simulé', color: 'bg-purple-500', icon: Send },
  delivered: { label: 'Délivré', color: 'bg-green-500', icon: CheckCircle },
  failed: { label: 'Échec', color: 'bg-red-500', icon: XCircle },
  cancelled: { label: 'Annulé', color: 'bg-gray-500', icon: X }
};

const SMSArchives = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    failed: 0
  });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const perPage = 20;

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/sms-mailing/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip: (page - 1) * perPage, limit: perPage }
      });
      
      let filteredCampaigns = response.data.campaigns || [];
      
      // Apply client-side filters
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredCampaigns = filteredCampaigns.filter(c => 
          c.name?.toLowerCase().includes(query) ||
          c.message?.toLowerCase().includes(query)
        );
      }
      
      if (filterStatus !== 'all') {
        filteredCampaigns = filteredCampaigns.filter(c => c.status === filterStatus);
      }
      
      if (filterType !== 'all') {
        filteredCampaigns = filteredCampaigns.filter(c => c.type === filterType);
      }
      
      // Filter by period
      if (filterPeriod !== 'all') {
        const now = new Date();
        let startDate;
        
        switch (filterPeriod) {
          case 'today':
            startDate = new Date(now.setHours(0, 0, 0, 0));
            break;
          case 'week':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'quarter':
            startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = null;
        }
        
        if (startDate) {
          filteredCampaigns = filteredCampaigns.filter(c => 
            new Date(c.created_at) >= startDate
          );
        }
      }
      
      // Filter by custom date range
      if (filterDateFrom) {
        filteredCampaigns = filteredCampaigns.filter(c => 
          new Date(c.created_at) >= new Date(filterDateFrom)
        );
      }
      if (filterDateTo) {
        filteredCampaigns = filteredCampaigns.filter(c => 
          new Date(c.created_at) <= new Date(filterDateTo + 'T23:59:59')
        );
      }
      
      setCampaigns(filteredCampaigns);
      setTotalCampaigns(response.data.count || filteredCampaigns.length);
      setTotalPages(Math.ceil((response.data.count || filteredCampaigns.length) / perPage));
      
      // Calculate stats
      const allCampaigns = response.data.campaigns || [];
      setStats({
        total: allCampaigns.length,
        sent: allCampaigns.filter(c => c.status === 'sent' || c.status === 'mock_sent').length,
        delivered: allCampaigns.reduce((sum, c) => sum + (c.delivered_count || 0), 0),
        failed: allCampaigns.filter(c => c.status === 'failed' || c.status === 'cancelled').length
      });
      
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Erreur lors du chargement des archives');
    } finally {
      setLoading(false);
    }
  }, [token, page, searchQuery, filterStatus, filterType, filterPeriod, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Reset filters
  const resetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterType('all');
    setFilterPeriod('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(1);
  };

  // Export data
  const handleExport = async (format) => {
    try {
      toast.info(`Export ${format.toUpperCase()} en cours...`);
      // TODO: Implement export API
      toast.success(`Export ${format.toUpperCase()} terminé`);
    } catch (error) {
      toast.error(`Erreur lors de l'export ${format.toUpperCase()}`);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <SMSLayout>
      <div className="space-y-6" data-testid="sms-archives-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Archives SMS
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Historique de toutes vos campagnes SMS
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchCampaigns}
              disabled={loading}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
            >
              <FileDown className="h-4 w-4 mr-2" />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                  <Archive className={`h-5 w-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats.total}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Campagnes
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                  <Send className={`h-5 w-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats.sent}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Envoyées
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                  <CheckCircle className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats.delivered}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    SMS délivrés
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
                  <XCircle className={`h-5 w-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats.failed}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Échecs
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  <Input
                    placeholder="Rechercher par nom ou contenu..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-9 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                </div>
              </div>
              
              {/* Period Quick Filter */}
              <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                <SelectTrigger className={`w-[180px] ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}>
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Période" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les périodes</SelectItem>
                  <SelectItem value="today">{"Aujourd'hui"}</SelectItem>
                  <SelectItem value="week">7 derniers jours</SelectItem>
                  <SelectItem value="month">30 derniers jours</SelectItem>
                  <SelectItem value="quarter">3 derniers mois</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className={`w-[150px] ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="sent">Envoyé</SelectItem>
                  <SelectItem value="mock_sent">Simulé</SelectItem>
                  <SelectItem value="scheduled">Programmé</SelectItem>
                  <SelectItem value="failed">Échec</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Type Filter */}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className={`w-[150px] ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="transactional">Transactionnel</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Toggle Advanced Filters */}
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Masquer' : 'Plus de filtres'}
              </Button>
              
              {/* Reset */}
              {(searchQuery || filterStatus !== 'all' || filterType !== 'all' || filterPeriod !== 'all' || filterDateFrom || filterDateTo) && (
                <Button
                  variant="ghost"
                  onClick={resetFilters}
                  className={isDarkMode ? 'text-slate-400 hover:text-white' : ''}
                >
                  <X className="h-4 w-4 mr-2" />
                  Réinitialiser
                </Button>
              )}
            </div>
            
            {/* Advanced Filters */}
            {showFilters && (
              <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-200'} flex items-center gap-4`}>
                <div className="flex items-center gap-2">
                  <Label className={isDarkMode ? 'text-slate-300' : ''}>Du</Label>
                  <Input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className={`w-40 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className={isDarkMode ? 'text-slate-300' : ''}>Au</Label>
                  <Input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className={`w-40 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Campaigns List */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
              Historique des campagnes
              {totalCampaigns > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {totalCampaigns} résultat{totalCampaigns > 1 ? 's' : ''}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : campaigns.length === 0 ? (
              <div className={`text-center py-12 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">Aucune campagne trouvée</p>
                <p className="text-sm mt-1">Modifiez vos filtres ou créez une nouvelle campagne</p>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className={`p-4 rounded-xl transition-colors ${
                      isDarkMode 
                        ? 'bg-[hsl(220,14%,18%)] hover:bg-[hsl(220,14%,20%)]' 
                        : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {campaign.name || 'Sans nom'}
                          </h3>
                          {(() => {
                            const config = statusConfig[campaign.status] || statusConfig.pending;
                            const Icon = config.icon;
                            return (
                              <Badge className={`${config.color} text-white flex items-center gap-1`}>
                                <Icon className="h-3 w-3" />
                                {config.label}
                              </Badge>
                            );
                          })()}
                          <Badge variant="outline" className={campaign.type === 'marketing' ? 'border-indigo-400 text-indigo-600' : 'border-emerald-400 text-emerald-600'}>
                            {campaign.type === 'marketing' ? 'Marketing' : 'Transactionnel'}
                          </Badge>
                        </div>
                        
                        <p className={`text-sm line-clamp-2 mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                          {campaign.message?.substring(0, 150)}{campaign.message?.length > 150 ? '...' : ''}
                        </p>
                        
                        <div className={`flex items-center gap-6 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {campaign.recipients_count || 0} destinataires
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-4 w-4" />
                            {campaign.sent_count || 0} envoyés
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(campaign.created_at)}
                          </span>
                          {campaign.sent_at && (
                            <span className="flex items-center gap-1">
                              <Send className="h-4 w-4" />
                              Envoyé le {formatDate(campaign.sent_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className={isDarkMode ? 'text-slate-400 hover:text-white' : ''}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`flex items-center justify-between mt-6 pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Page {page} sur {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SMSLayout>
  );
};

export default SMSArchives;
